import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { accountMutations } from '@/features/accounts/accounts.queries';
import { executeMutation } from '@/utils/executeMutation';
import { AdminAccount } from '@certhon/models';
import { formatDistanceToNow } from 'date-fns';
import { ChevronRight, RotateCcw, Shield } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';
import { ExpirationStatus } from './ExpirationStatus';

interface SecuritySectionProps {
  account: AdminAccount;
}

export function SecuritySection({ account }: SecuritySectionProps) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border"
    >
      <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center gap-2 p-3">
        <ChevronRight
          className={cn('h-4 w-4 transition-transform', open && 'rotate-90')}
        />
        <Shield className="h-4 w-4" />
        <span className="font-medium">Security Details</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Password Strength</dt>
          <dd>
            <PasswordStrengthIndicator strength={account.password_strength} />
          </dd>

          <dt className="text-muted-foreground">Password Changed</dt>
          <dd>
            {account.password_date
              ? formatDistanceToNow(new Date(account.password_date), {
                  addSuffix: true,
                })
              : '-'}
          </dd>

          <dt className="text-muted-foreground">Last Login</dt>
          <dd>
            {account.last_successful_login
              ? formatDistanceToNow(new Date(account.last_successful_login), {
                  addSuffix: true,
                })
              : 'Never'}
          </dd>

          <dt className="text-muted-foreground">Failed Attempts</dt>
          <dd
            className={cn(
              'flex items-center gap-2',
              account.auth_attempts > 10
                ? 'text-destructive font-semibold'
                : account.auth_attempts > 3
                  ? 'text-warning'
                  : '',
            )}
          >
            {account.auth_attempts}
            {account.auth_attempts > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() =>
                  executeMutation(accountMutations.update(), {
                    recnum: account.recnum,
                    auth_attempts: 0,
                  })
                }
              >
                <RotateCcw className="mr-1 h-3 w-3" />
                Reset
              </Button>
            )}
          </dd>

          <dt className="text-muted-foreground">Account Expires</dt>
          <dd>
            <ExpirationStatus expiresOn={account.expires_on} />
          </dd>
        </dl>
      </CollapsibleContent>
    </Collapsible>
  );
}
